// ── Composition root (DI) ─────────────────────────────────────────
// Ponto ÚNICO de instanciação da aplicação: cada service recebe seu(s)
// repositório(s) concreto(s) (adapters Supabase/HTTP) via construtor. Trocar a
// infra (ex.: Android, outra fonte de dados, mock em teste) = trocar os
// `new Supabase*` aqui, sem tocar no núcleo. A fachada @app (app/<domínio>.ts)
// apenas re-exporta os métodos destas instâncias.

// Services (núcleo puro — sem Supabase)
import { LeadService, LeadFlowService } from '@modules/leads';
import { AgendaService, MeetingService } from '@modules/agenda';
import { ActivitiesService, FlowConfigService, CadenceService } from '@modules/activity';
import { CommissionService } from '@modules/commission';
import {
  ConfigService,
  WorkspaceConfigService,
  TagService,
  PipelineMembersService,
  FilterOptionsService,
} from '@modules/config';
import { DashboardService } from '@modules/dashboard';
import { HistoryService } from '@modules/history';
import { GoalsService } from '@modules/goals';
import { InviteService } from '@modules/invites';
import { SalesService } from '@modules/sales';
import { PlansService } from '@modules/plans';
import { EmpresaService } from '@modules/empresas';
import { CsService } from '@modules/cs';
import { CardService } from '@modules/cards';
import { MktService } from '@modules/mkt';
import { WorkspaceWebhookService } from '@modules/webhooks';
import { StorageService } from '@modules/storage';
import { NotificationsService } from '@modules/notifications';
import { BluehubService } from '@modules/bluehub';

// Adapters (infra Supabase / HTTP)
import { SupabaseLeadRepository } from '@infra/supabase/repositories/SupabaseLeadRepository';
import { SupabaseAgendaRepository } from '@infra/supabase/repositories/SupabaseAgendaRepository';
import { SupabaseMeetingRepository } from '@infra/supabase/repositories/SupabaseMeetingRepository';
import { SupabaseActivitiesRepository } from '@infra/supabase/repositories/SupabaseActivitiesRepository';
import { SupabaseFlowConfigRepository } from '@infra/supabase/repositories/SupabaseFlowConfigRepository';
import { SupabaseCadenceRepository } from '@infra/supabase/repositories/SupabaseCadenceRepository';
import { SupabaseCommissionRepository } from '@infra/supabase/repositories/SupabaseCommissionRepository';
import { SupabaseConfigRepository } from '@infra/supabase/repositories/SupabaseConfigRepository';
import { SupabasePipelinesRepository } from '@infra/supabase/repositories/SupabasePipelinesRepository';
import { SupabaseUsersRepository } from '@infra/supabase/repositories/SupabaseUsersRepository';
import { SupabaseWorkspaceConfigRepository } from '@infra/supabase/repositories/SupabaseWorkspaceConfigRepository';
import { SupabaseTagRepository } from '@infra/supabase/repositories/SupabaseTagRepository';
import { SupabasePipelineMembersRepository } from '@infra/supabase/repositories/SupabasePipelineMembersRepository';
import { SupabaseFilterOptionsRepository } from '@infra/supabase/repositories/SupabaseFilterOptionsRepository';
import { SupabaseDashboardRepository } from '@infra/supabase/repositories/SupabaseDashboardRepository';
import { SupabaseHistoryRepository } from '@infra/supabase/repositories/SupabaseHistoryRepository';
import { SupabaseGoalsRepository } from '@infra/supabase/repositories/SupabaseGoalsRepository';
import { SupabaseMktRepository } from '@infra/supabase/repositories/SupabaseMktRepository';
import { MktWebhookClient } from '@infra/http/MktWebhookClient';
import { BluehubApiClient } from '@infra/http/BluehubApiClient';
import { ListaVidasApiClient } from '@infra/http/ListaVidasApiClient';
import { SupabaseWorkspaceWebhookGateway } from '@infra/supabase/repositories/SupabaseWorkspaceWebhookGateway';
import { SupabaseWorkspaceWebhookRepository } from '@infra/supabase/repositories/SupabaseWorkspaceWebhookRepository';
import { SupabaseStorageRepository } from '@infra/supabase/repositories/SupabaseStorageRepository';
import { SupabaseNotificationsRepository } from '@infra/supabase/repositories/SupabaseNotificationsRepository';
import { SupabaseInviteRepository } from '@infra/supabase/repositories/SupabaseInviteRepository';
import { SupabaseSalesRepository } from '@infra/supabase/repositories/SupabaseSalesRepository';
import { SupabasePlansRepository } from '@infra/supabase/repositories/SupabasePlansRepository';
import { SupabaseCsRepository } from '@infra/supabase/repositories/SupabaseCsRepository';
import { SupabaseEmpresaRepository } from '@infra/supabase/repositories/SupabaseEmpresaRepository';
import { SupabaseCardRepository } from '@infra/supabase/repositories/SupabaseCardRepository';
import { SupabasePipelinesConfigRepository } from '@infra/supabase/repositories/SupabasePipelinesConfigRepository';
import { SupabaseSuperAdminRepository } from '@infra/supabase/repositories/SupabaseSuperAdminRepository';
import { SupabasePanelMetricsRepository } from '@infra/supabase/repositories/SupabasePanelMetricsRepository';
import { SupabaseSessionRepository } from '@infra/supabase/repositories/SupabaseSessionRepository';
import { SupabaseInfraMetricsRepository } from '@infra/supabase/repositories/SupabaseInfraMetricsRepository';
import { SupabaseTeamUsageRepository } from '@infra/supabase/repositories/SupabaseTeamUsageRepository';
import { SupabaseFinanceGlobalRepository } from '@infra/supabase/repositories/SupabaseFinanceGlobalRepository';
import { SupabaseAuthGateway } from '@infra/supabase/auth/SupabaseAuthGateway';
import { PasswordResetService } from '@modules/auth/services/passwordResetService';
import { SupabasePasswordResetRepository } from '@infra/supabase/repositories/SupabasePasswordResetRepository';
import { SupabaseRealtimeGateway } from '@infra/supabase/realtime/SupabaseRealtimeGateway';

// ── MKT ───────────────────────────────────────────────────────────
// Criado cedo: a agenda injeta mktService.notifyMktConversion no seu adapter.
export const mktService = new MktService({
  leadRepo: new SupabaseMktRepository(),
  webhook: new MktWebhookClient(),
});

// ── Webhooks de saída por workspace (Config/87) ───────────────────
// Criado cedo pelo mesmo motivo do mktService: a agenda injeta o `notify`
// no seu adapter para avisar do agendamento.
export const workspaceWebhookService = new WorkspaceWebhookService({
  gateway: new SupabaseWorkspaceWebhookGateway(),
  repo: new SupabaseWorkspaceWebhookRepository(),
});

// ── Leads ─────────────────────────────────────────────────────────
const leadRepository = new SupabaseLeadRepository();
export const leadService = new LeadService(leadRepository);

// ── Agenda ────────────────────────────────────────────────────────
export const agendaService = new AgendaService(
  new SupabaseAgendaRepository(mktService.notifyMktConversion, workspaceWebhookService.notify),
);
export const meetingService = new MeetingService(new SupabaseMeetingRepository());

// ── Activity ──────────────────────────────────────────────────────
export const activitiesService = new ActivitiesService(new SupabaseActivitiesRepository());
export const flowConfigService = new FlowConfigService(new SupabaseFlowConfigRepository());
export const cadenceService = new CadenceService(new SupabaseCadenceRepository());

// ── Commission ────────────────────────────────────────────────────
export const commissionService = new CommissionService(new SupabaseCommissionRepository());

// ── Config ────────────────────────────────────────────────────────
export const configService = new ConfigService(new SupabaseConfigRepository());
export const pipelinesRepo = new SupabasePipelinesRepository();
export const usersRepo = new SupabaseUsersRepository();
export const workspaceConfigService = new WorkspaceConfigService(new SupabaseWorkspaceConfigRepository());
export const tagService = new TagService(new SupabaseTagRepository());
export const pipelineMembersService = new PipelineMembersService(new SupabasePipelineMembersRepository());
export const filterOptionsService = new FilterOptionsService({
  repo: new SupabaseFilterOptionsRepository(),
  getWorkspaceOrigins: configService.getWorkspaceOrigins,
  listTags: tagService.listTags,
  listPipelinesWithLossReasons: workspaceConfigService.listPipelinesWithLossReasons,
  listSdrs: agendaService.listSdrs,
  listCollaborators: async (workspaceId: string) => {
    const roleLabels: Record<string, string> = {
      sdr: 'Colaborador',
      aex: 'AEX',
      gestor: 'Gestor',
    };
    const roleLists = await Promise.all(
      Object.keys(roleLabels).map(async role => ({
        role,
        profiles: await agendaService.listUsersByRole(workspaceId, role),
      })),
    );
    const byId = new Map<string, Record<string, unknown> & { roles: Set<string> }>();
    roleLists.forEach(({ role, profiles }) => {
      profiles.forEach(profile => {
        const existing = byId.get(profile.id);
        if (existing) {
          existing.roles.add(role);
          return;
        }
        byId.set(profile.id, { ...profile, roles: new Set([role]) });
      });
    });
    return [...byId.values()]
      .map(({ roles, ...profile }) => ({
        ...profile,
        role: [...roles][0] || null,
        description: [...roles].map(role => roleLabels[role] || role).join(' · '),
      }))
      .sort((a, b) => String((a as Record<string, unknown>).nome || '').localeCompare(
        String((b as Record<string, unknown>).nome || ''),
        'pt-BR',
      ));
  },
  listClosers: agendaService.listClosers,
});

// ── Dashboard ─────────────────────────────────────────────────────
export const dashboardService = new DashboardService(
  new SupabaseDashboardRepository(configService.getWorkspaceOrigins, pipelinesRepo.listPipelineIds),
);

// ── History ───────────────────────────────────────────────────────
export const historyService = new HistoryService(new SupabaseHistoryRepository());

// ── Goals ─────────────────────────────────────────────────────────
export const goalsService = new GoalsService(new SupabaseGoalsRepository());

// ── Storage ───────────────────────────────────────────────────────
export const storageService = new StorageService(new SupabaseStorageRepository());

// ── Notifications (lembretes de atividade/reunião — Config/102-104) ─
// Só leitura/preferência: quem CRIA notificação são os triggers do banco.
export const notificationsService = new NotificationsService(new SupabaseNotificationsRepository());

// ── Invites ───────────────────────────────────────────────────────
export const inviteService = new InviteService(new SupabaseInviteRepository());

// ── Sales ─────────────────────────────────────────────────────────
export const salesService = new SalesService(new SupabaseSalesRepository());
export const plansService = new PlansService(new SupabasePlansRepository());

// ── Empresas (entidade escopada por workspace — Enterprise) ───────
export const empresaService = new EmpresaService(new SupabaseEmpresaRepository());

// ── BlueHub (API do Hub: empresa, vidas, contrato, cobrança) ──────
//  O adapter fala com /api/bluehub no nosso servidor, que é quem guarda a
//  X-API-Key. Nenhuma credencial do Hub existe no bundle.
export const bluehubService = new BluehubService(new BluehubApiClient());

// ── Lista de vidas (planilha Google Sheets, via /api/lista-vidas) ─
//  Domínio separado do CS: sem workspace, sem service. O servidor guarda a
//  credencial do Google e escreve na planilha.
export const listaVidasApi = new ListaVidasApiClient();

// ── CS (pós-venda Enterprise: contrato, vidas, gatilhos) ──────────
export const csService = new CsService(new SupabaseCsRepository());

// ── Cards ─────────────────────────────────────────────────────────
export const cardService = new CardService(new SupabaseCardRepository());

// ── PipelinesConfig (data-access de configuração — sem service) ───
export const pipelinesConfigRepo = new SupabasePipelinesConfigRepository();

// ── SuperAdmin (data-access administrativo — sem service) ─────────
export const superAdminRepo = new SupabaseSuperAdminRepository();

// ── PanelMetrics (métricas do Meu Painel — sem service) ───────────
export const panelMetricsRepo = new SupabasePanelMetricsRepository();

// ── Session (leituras de identidade — sem service) ────────────────
export const sessionRepo = new SupabaseSessionRepository();

// ── InfraMetrics (Supabase Metrics API + VM Linux) ────────────────
export const infraMetricsRepo = new SupabaseInfraMetricsRepository();

// ── TeamUsage (presença + produção da equipe — sem service) ───────
export const teamUsageRepo = new SupabaseTeamUsageRepository();
export const financeGlobalRepo = new SupabaseFinanceGlobalRepository();

// ── Auth (gateway, sem service — passthrough tipado) ──────────────
export const authGateway = new SupabaseAuthGateway();

// Reset de senha: tem service (validações) porque o gateway é um
// passthrough e aqui há regra — mínimo de caracteres, confirmação e o
// veto à senha padrão.
export const passwordResetService = new PasswordResetService(
  new SupabasePasswordResetRepository(),
);

// ── Realtime (gateway) ────────────────────────────────────────────
export const realtimeGateway = new SupabaseRealtimeGateway();

// ── LeadFlow (orquestração de aplicação — compõe leads/cards/tag/mkt/activity) ─
// Criado por último: depende de instâncias de outros domínios já montadas acima.
export const leadFlowService = new LeadFlowService({
  leadRepo: leadRepository,
  notifyMktConversion: mktService.notifyMktConversion,
  addTagToLead: tagService.addTagToLead,
  assignActiveCardsOwner: cardService.assignActiveCardsOwner,
  deactivateCardsByLead: cardService.deactivateCardsByLead,
  moveCardToStage: cardService.moveCardToStage,
  markCardLost: cardService.markCardLost,
  getStageNames: pipelinesConfigRepo.getStageNames,
  listPendingActivityIdsForCard: activitiesService.listPendingActivityIdsForCard,
  cancelActivitiesByIds: activitiesService.cancelActivitiesByIds,
  // Enterprise: import CSV vincula cada lead à entidade empresa (find-or-create
  // por nome normalizado — o insert colide no índice único e devolve a existente).
  resolveEmpresaId: async (workspaceId, nome) => {
    const empresa = await empresaService.createEmpresa({ workspaceId, nome });
    return empresa?.id ?? null;
  },
});
